import { Flex, Switch, Input, Checkbox, Tooltip, Button, Tag, Dropdown, Space, Popconfirm } from 'antd'
import { RightOutlined, DownOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle, faEdit, faEllipsisV, faEye, faToggleOff } from '@fortawesome/pro-solid-svg-icons'
import { useState, useMemo } from 'react'
import { red } from '@ant-design/colors'
import * as routeAddModel from '../routeAddModel'
import { AddJobsRecord, Route, RouteJob, RoutingType } from '../../routingApi'
import { getGeocoding } from '../../Mappers/mapbox'
import { useUpdateJobPropertyGeolocationMutation } from '../../routingQueries'
import { getFilteredJobs, hasJobError, hasNoWorkAreas, hasNoApprovedWorkAreas, getSelectableFilteredJobIds, getSelectableUnroutedJobIds, areAllJobsSelected } from '../routeAddModel'

export type JobsListSectionProps = {
  jobs: AddJobsRecord[] | undefined
  routeJobIds?: number[]
  filters: routeAddModel.AddRouteJobsFilters
  setFilters: (filters: routeAddModel.AddRouteJobsFilters) => void
  selectedJobs: number[]
  toggleJobSelection: (jobId: number) => void
  addJobsToSelection: (jobIds: number[]) => void
  removeJobsFromSelection: (jobIds: number[]) => void
  setSelectedJobForAddress: (job: AddJobsRecord) => void
  setIsJobAddressModalOpen: (isOpen: boolean) => void
  onViewJob: (jobId: number) => void
  onDeactivateJob: (jobId: number) => void
  routes: Route[] | undefined
  allRouteJobs: RouteJob[] | undefined
}

const JobsListSection = ({
  jobs,
  routeJobIds = [],
  filters,
  setFilters,
  selectedJobs,
  toggleJobSelection,
  addJobsToSelection,
  removeJobsFromSelection,
  setSelectedJobForAddress,
  setIsJobAddressModalOpen,
  onViewJob,
  onDeactivateJob,
  routes,
  allRouteJobs,
}: JobsListSectionProps) => {

  const [hoveredJobId, setHoveredJobId] = useState<number | null>(null)
  const [openDropdownJobId, setOpenDropdownJobId] = useState<number | null>(null)
  const [expandedRouteJobIds, setExpandedRouteJobIds] = useState<Set<number>>(new Set())
  const { mutate: updateJobPropertyGeolocation, isLoading: isUpdatingJobPropertyGeolocation } = useUpdateJobPropertyGeolocationMutation()

  const jobRoutesMap = useMemo(() => {
    const map = new Map<number, Route[]>()
    if (!allRouteJobs || !routes) return map
    allRouteJobs.forEach(rj => {
      if (rj.jobId) {
        const route = routes.find(r => r.id === rj.routeId)
        if (route) {
          const existing = map.get(rj.jobId) || []
          map.set(rj.jobId, [...existing, route])
        }
      }
    })
    return map
  }, [allRouteJobs, routes])

  const filteredJobs = useMemo(
    () => getFilteredJobs(jobs || [], filters, selectedJobs, routeJobIds),
    [jobs, filters, selectedJobs, routeJobIds]
  )

  const selectableFilteredJobIds = useMemo(
    () => getSelectableFilteredJobIds(jobs || [], filters, selectedJobs),
    [jobs, filters, selectedJobs]
  )

  const selectableUnroutedJobIds = useMemo(
    () => getSelectableUnroutedJobIds(jobs || [], filters, selectedJobs, jobRoutesMap),
    [jobs, filters, selectedJobs, jobRoutesMap]
  )

  const allSelectableSelected = areAllJobsSelected(selectableFilteredJobIds, selectedJobs)

  const handleSelectAllToggle = () => {
    if (allSelectableSelected) {
      removeJobsFromSelection(selectableFilteredJobIds)
      if (filters.showSelectedJobsOnly) {
        setFilters({ ...filters, showSelectedJobsOnly: false })
      }
    } else {
      addJobsToSelection(selectableFilteredJobIds)
    }
  }

  const handleSelectUnrouted = () => {
    addJobsToSelection(selectableUnroutedJobIds)
    if (filters.showSelectedJobsOnly) {
      setFilters({ ...filters, showSelectedJobsOnly: false })
    }
  }
  return (
    <Flex
      vertical
      style={{
        width: '400px',
        borderLeft: '1px solid #f0f0f0',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Search Input and Selected Jobs Filter */}
      <div style={{ 
        padding: '16px', 
        borderBottom: '1px solid #f0f0f0',
        backgroundColor: 'white',
        position: 'sticky',
        top: 0,
        zIndex: 1
      }}>
        <Input.Search
          id="search-jobs-input"
          placeholder="Search by job, client, or address"
          style={{ width: '100%', marginBottom: '12px' }}
          onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
          value={filters.searchTerm}
        />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <div id="selected-jobs-filter" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Switch
              checked={filters.showSelectedJobsOnly}
              onChange={(checked) => setFilters({ ...filters, showSelectedJobsOnly: checked })}
            />
            <span>Selected Jobs</span>
          </div>
          <Flex gap={8}>
            <Button
              id="select-unrouted-jobs-button"
              size="small"
              disabled={selectableUnroutedJobIds.length === 0}
              onClick={handleSelectUnrouted}
            >
              Select Unrouted
            </Button>
            <Button
              id="select-all-jobs-button"
              size="small"
              disabled={selectableFilteredJobIds.length === 0}
              onClick={handleSelectAllToggle}
            >
              {allSelectableSelected ? 'Deselect All' : 'Select All'}
            </Button>
          </Flex>
        </div>
      </div>

      {/* Scrollable Job List */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px', paddingBottom: '65px' }}>
        <Flex vertical gap="middle">
          {(() => {
            const clientRecords = routeAddModel.getClientsJobsRecords(filteredJobs)

            if (clientRecords.length === 0) {
              return (
                <Flex
                  vertical
                  align="center"
                  justify="center"
                  style={{
                    padding: '32px 16px',
                    textAlign: 'center',
                    color: '#666'
                  }}
                >
                  <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                    No Jobs
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    Try adjusting the filters to see more jobs
                  </div>
                </Flex>
              )
            }

            return clientRecords.map((clientRecord) => (
            <div key={clientRecord.customerId}>
              <div style={{ 
                fontWeight: 'bold',
                fontSize: '14px',
                marginBottom: '8px',
                color: '#666'
              }}>
                {clientRecord.customerDisplayName}
              </div>
              {clientRecord.jobs.map((job) => (
                <Flex
                  key={job.jobId}
                  vertical
                  style={{
                    padding: '8px',
                    paddingBottom: '4px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    backgroundColor: selectedJobs.includes(job.jobId) ? '#FFCABD' : 'white',
                    cursor: 'pointer',
                    marginBottom: '4px',
                    fontSize: '12px',
                    opacity: (hasJobError(job) || hasNoWorkAreas(job) || hasNoApprovedWorkAreas(job)) ? 0.7 : 1
                  }}
                  onClick={(e) => {
                    if (!(e.target as HTMLElement).closest('.ant-checkbox-wrapper')) {
                      if (!hasNoWorkAreas(job) && !hasNoApprovedWorkAreas(job)) {
                        toggleJobSelection(job.jobId)
                      }
                    }
                  }}
                >
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap={8}>
                      <Checkbox
                        checked={selectedJobs.includes(job.jobId)}
                        onChange={(e) => {
                          e.stopPropagation()
                          if (!hasJobError(job) && !hasNoWorkAreas(job) && !hasNoApprovedWorkAreas(job)) {
                            toggleJobSelection(job.jobId)
                          }
                        }}
                        disabled={hasJobError(job) || hasNoWorkAreas(job) || hasNoApprovedWorkAreas(job)}
                      />
                      <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                        {job.jobName}
                      </div>
                      {job.isSold === false && (
                        <Tooltip title="This job isn't sold, so it no longer appears in the job list. It will keep generating scheduled events while it stays on the route." placement="top">
                          <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#faad14', fontSize: '13px' }} />
                        </Tooltip>
                      )}
                    </Flex>
                    <Dropdown
                      trigger={['click']}
                      open={openDropdownJobId === job.jobId}
                      onOpenChange={(open) => setOpenDropdownJobId(open ? job.jobId : null)}
                      menu={{
                        items: [
                          {
                            key: 'view-job',
                            label: <Space><FontAwesomeIcon icon={faEye} />View Job</Space>,
                            onClick: (e) => {
                              e.domEvent.stopPropagation()
                              setOpenDropdownJobId(null)
                              onViewJob(job.jobId)
                            }
                          },
                          ...(job.type !== '1' ? [{
                            key: 'deactivate',
                            label: (
                              <div onClick={(e) => e.stopPropagation()}>
                                <Popconfirm
                                  title="Mark Job as Inactive?"
                                  description={`Are you sure you want to mark "${job.jobName}" as inactive?`}
                                  okText="Yes"
                                  cancelText="No"
                                  onConfirm={() => {
                                    setOpenDropdownJobId(null)
                                    onDeactivateJob(job.jobId)
                                  }}
                                  onCancel={() => setOpenDropdownJobId(null)}
                                  onPopupClick={(e) => e.stopPropagation()}
                                >
                                  <Space>
                                    <FontAwesomeIcon icon={faToggleOff} />Mark Job as Inactive
                                  </Space>
                                </Popconfirm>
                              </div>
                            )
                          }] : [])
                        ]
                      }}
                    >
                      <Button
                        type="text"
                        icon={<FontAwesomeIcon icon={faEllipsisV} />}
                        onClick={e => e.stopPropagation()}
                      />
                    </Dropdown>
                  </Flex>
                  
                  {(!job.address1 || !job.city || !job.state || !job.zip || !job.latitude || !job.longitude) && (
                    <div 
                      style={{ 
                        color: red.primary, 
                        fontSize: '11px', 
                        marginTop: '4px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        cursor: 'pointer'
                      }}
                      onClick={async (e) => {
                        e.stopPropagation()
                        toggleJobSelection(job.jobId)
                      }}
                    >
                      <FontAwesomeIcon icon={faExclamationCircle} style={{ fontSize: '12px' }} />
                      {!job.address1 || !job.city || !job.state || !job.zip 
                        ? "Missing Address" 
                        : "Missing Location"}
                          <Tooltip title="We are missing the geolocation of the address.  Clicking this button will help us resolve this and add the job to the map." placement="top">
                            <Button
                              id="resolve-address-button"
                              type="primary"
                              size="small" 
                              style={{ marginLeft: '8px' }} 
                              loading={isUpdatingJobPropertyGeolocation}
                              onClick={async (e) => {
                                e.stopPropagation()
                                const result = await getGeocoding([job.address1, job.address2, job.city, job.state, job.zip].filter(Boolean).join(' '))
                                if(result){
                                  updateJobPropertyGeolocation({
                                    propertyId: job?.propertyId,
                                    nickname: '',
                                    address1: result.properties.context.address.name,
                                    address2: null,
                                    city: result.properties.context.place.name,
                                    state: result.properties.context.region.region_code,
                                    zip: result.properties.context.postcode.name,
                                    county: result.properties.context.district.name,
                                    countryCode: result.properties.context.country.country_code,
                                    latitude: result.geometry.coordinates[1],
                                    longitude: result.geometry.coordinates[0]
                                  })
                                }
                                else{
                                  setSelectedJobForAddress(job)
                                  setIsJobAddressModalOpen(true)
                                }
                              }}>
                                Resolve
                            </Button>
                          </Tooltip>
                    </div>
                  )}
                  
                  {job.address1 && job.city && job.state && job.zip && (
                    <div id="edit-address-section" style={{ position: 'relative' }}>
                      <span
                        style={{ 
                          color: '#666',
                          textDecoration: hoveredJobId === job.jobId ? 'underline' : 'none',
                          transition: 'text-decoration 0.2s ease',
                          cursor: 'pointer',
                          display: 'inline-block'
                        }}
                        onMouseEnter={() => setHoveredJobId(job.jobId)}
                        onMouseLeave={() => setHoveredJobId(null)}
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedJobForAddress(job)
                          setIsJobAddressModalOpen(true)
                        }}
                      >
                        {job.address1}
                        {hoveredJobId === job.jobId && (
                          <FontAwesomeIcon 
                            icon={faEdit} 
                            style={{ 
                              marginLeft: '8px',
                              fontSize: '12px',
                              color: '#1890ff'
                            }} 
                          />
                        )}
                      </span>
                      <div style={{ color: '#666' }}>
                        <span
                          style={{ 
                            textDecoration: hoveredJobId === job.jobId ? 'underline' : 'none',
                            transition: 'text-decoration 0.2s ease',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={() => setHoveredJobId(job.jobId)}
                          onMouseLeave={() => setHoveredJobId(null)}
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedJobForAddress(job)
                            setIsJobAddressModalOpen(true)
                          }}
                        >
                          {job.city}, {job.state} {job.zip}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <Flex justify="space-between" align="center" style={{ marginTop: '8px' }}>
                    <Flex align="center" gap={8}>
                      <div style={{ color: '#666', fontSize: '12px' }}>
                        Work Areas {job.workareaCount}
                      </div>
                      {!hasNoWorkAreas(job) && hasNoApprovedWorkAreas(job) && (
                        <Tag style={{ fontSize: '11px', color: '#000' }}>No Approved Work Areas</Tag>
                      )}
                    </Flex>
                  </Flex>
                  <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {(() => {
                      const routes = jobRoutesMap.get(job.jobId)
                      if (!routes || routes.length === 0) return null
                      const isExpanded = expandedRouteJobIds.has(job.jobId)
                      const toggleExpand = () => {
                        setExpandedRouteJobIds(prev => {
                          const newSet = new Set(prev)
                          if (newSet.has(job.jobId)) {
                            newSet.delete(job.jobId)
                          } else {
                            newSet.add(job.jobId)
                          }
                          return newSet
                        })
                      }
                      return (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: '100%',
                            border: '1px solid #d9d9d9',
                            borderRadius: '4px',
                            backgroundColor: '#fff'
                          }}
                        >
                          <div
                            onClick={toggleExpand}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 8px',
                              cursor: 'pointer',
                              fontSize: '11px'
                            }}
                          >
                            {isExpanded ? <DownOutlined style={{ fontSize: '8px' }} /> : <RightOutlined style={{ fontSize: '8px' }} />}
                            <span>Routes ({routes.length})</span>
                          </div>
                          {isExpanded && (
                            <div style={{ padding: '4px 8px', borderTop: '1px solid #d9d9d9' }}>
                              <Flex vertical gap={4}>
                                {routes.map((route) => (
                                  <Flex key={route.id} justify="space-between" align="center">
                                    <span style={{ fontSize: '11px' }}>{route.name}</span>
                                    <Flex gap={4}>
                                      <Tag
                                        style={{
                                          fontSize: '10px',
                                          borderRadius: '12px',
                                          margin: 0,
                                          ...(route.routeType === RoutingType.Planned && {
                                            borderColor: '#531DAB',
                                            backgroundColor: '#F9F0FF',
                                            color: '#531DAB'
                                          }),
                                          ...(route.routeType === RoutingType.OnDemand && {
                                            borderColor: '#08979C',
                                            backgroundColor: '#E6FFFB',
                                            color: '#08979C'
                                          })
                                        }}
                                      >
                                        {route.routeType === RoutingType.Planned ? 'Planned' : 'On Demand'}
                                      </Tag>
                                      <Tag
                                        style={{
                                          fontSize: '10px',
                                          borderRadius: '12px',
                                          margin: 0,
                                          ...(route.active ? {
                                            borderColor: '#389E0D',
                                            backgroundColor: '#F6FFED',
                                            color: '#389E0D'
                                          } : {
                                            borderColor: '#8C8C8C',
                                            backgroundColor: '#FAFAFA',
                                            color: '#8C8C8C'
                                          })
                                        }}
                                      >
                                        {route.active ? 'Active' : 'Inactive'}
                                      </Tag>
                                    </Flex>
                                  </Flex>
                                ))}
                              </Flex>
                            </div>
                          )}
                        </div>
                      )
                    })()}
                    {hasNoWorkAreas(job) && (
                      <Tag style={{ fontSize: '11px', color: '#000' }}>No Work Areas</Tag>
                    )}
                  </div>
                </Flex>
              ))}
            </div>
            ))
          })()}
        </Flex>
      </div>
    </Flex>
  )
}

export default JobsListSection
